import api from "../../axiosSetup";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/redux/store"; // adjust the path as needed
import { IRole } from "../../../types/IRole";
import { IUser } from "../../../types/IUser";
import { ISignatory } from "../../../types/ISignatory";
import { getApiClient } from "@/utils/getApiClient";

// Define the Initial State
type UsersState = {
  signers: ISignatory[];
  signerMutable: ISignatory | null,
  loading: boolean;
  error: string | null;
};

const initialState: UsersState = {
  signers: [],
  signerMutable: null,
  loading: false,
  error: null,
};

// Create the users slice
const signatoriesSlice = createSlice({
  name: "signers",
  initialState,
  reducers: {
    setSignatories: (state, action: PayloadAction<ISignatory[]>) => {
      state.signers = action.payload;
    },
    addSignatory: (state, action: PayloadAction<ISignatory>) => {
      state.signers.push(action.payload);
    },
    removeSignatory: (state, action: PayloadAction<string>) => {
      state.signers = state.signers.filter(signer => signer._id !== action.payload);
    },
    setSignerMutable: (state, action: PayloadAction<ISignatory>) => {
      state.signerMutable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET BY ID
      .addCase(getSignerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSignerById.fulfilled, (state, action: PayloadAction<ISignatory>) => {
        state.loading = false;
        state.signerMutable = action.payload;

        // keep list in sync: update if exists, otherwise add
        const i = state.signers.findIndex(s => s._id === action.payload._id);
        if (i >= 0) {
          state.signers[i] = action.payload;
        } else {
          state.signers.push(action.payload);
        }
      })
      .addCase(getSignerById.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch signatory";
      })

      .addCase(fetchSignatories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSignatories.fulfilled, (state, action) => {
        state.loading = false;
        state.signers = action.payload;
      })
      .addCase(fetchSignatories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create User
      .addCase(createSigner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSigner.fulfilled, (state, action) => {
        state.loading = false;
        state.signers.push(action.payload);
      })
      .addCase(createSigner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update User
      .addCase(updateSigner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSigner.fulfilled, (state, action: PayloadAction<ISignatory>) => {
        state.loading = false;
        state.signers = state.signers.map((signer) =>
          signer._id === action.payload._id ? action.payload : signer
        );
      })
      .addCase(updateSigner.rejected, (state, action) => {
        state.loading = false;
        // Always store a string
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : (action.payload as any)?.message || 'Failed to update user';
      })

      // Delete User (1/3)
      .addCase(deleteSigner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSigner.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.signers = state.signers.filter(signer => signer._id !== action.payload);
      })
      .addCase(deleteSigner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update user";
      });
  },
});

export const fetchSignatories = createAsyncThunk<
  ISignatory[],             // Return type
  void,                // Argument type (no arg in this case)
  { rejectValue: string } // Reject type
>(
  "signatories/fetchSignatories",
  async (_, { rejectWithValue }) => {
    try {
      const client = getApiClient();
      
      const response = await client.get(`/api/signatories`);
      console.log('response.data.signers', response.data.signers)
      return response.data.signers;
    } catch (error) {
      return rejectWithValue("Failed to fetch signers");
    }
  }
);

export const createSigner = createAsyncThunk<
  ISignatory, // ✅ Return type (User)
  ISignatory, // ✅ Argument type (New User)
  { rejectValue: string } // ✅ Error type
>(
  "signatories/createSigner",
  async (signer, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/signatories", signer); // ✅ Send user data in body
      return response.data.signer; // ✅ Return user from API response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create signer");
    }
  }
);

type RejectStr = { rejectValue: string };

// --- READ (by id) --------------------------------------------

/**
 * Get a signatory by id
 * returns: ISignatory
 */
export const getSignerById = createAsyncThunk<
  ISignatory,
  { id: string },
  RejectStr
>("signatories/getById", async ({ id }, { rejectWithValue }) => {
  try {
    const res = await api.get(`/api/signatories/${id}`);
    return res.data.signer;
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ??
      err?.message ??
      "Failed to fetch signatory";
    return rejectWithValue(msg);
  }
});

// --- UPDATE ---------------------------------------------------

/**
 * Update a signatory
 * body: { signerName?: string; email?: string }
 * returns: ISignatory
 */
export const updateSigner = createAsyncThunk<
  ISignatory,
  { id: string; updates: any },
  RejectStr
>("signatories/update", async ({ id, updates }, { rejectWithValue }) => {
  try {
    const res = await api.patch(`/api/signatories/${id}`, updates, {
      // If you rely on cookies/session, include credentials option here
    });
    return res.data.signer;
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ??
      err?.message ??
      "Failed to update signatory";
    return rejectWithValue(msg);
  }
});

// --- DELETE ---------------------------------------------------

/**
 * Delete a signatory
 * returns: deleted id (so reducers can remove it from state)
 */
export const deleteSigner = createAsyncThunk<
  string,
  { id: string },
  RejectStr
>("signatories/delete", async ({ id }, { rejectWithValue }) => {
  try {
    await api.delete(`/api/signatories/${id}`);
    return id; // return the id so reducer can remove from list
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ??
      err?.message ??
      "Failed to delete signatory";
    return rejectWithValue(msg);
  }
});

export const { setSignatories, addSignatory, removeSignatory, setSignerMutable } = signatoriesSlice.actions;
export default signatoriesSlice.reducer;
